import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Container,
	Typography,
	CircularProgress,
	Alert,
	Box,
	Button,
	TextField,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Select,
	MenuItem,
	FormHelperText,
	InputLabel,
	FormControl,
	Snackbar,
} from "@mui/material";
import customHttp from "../../services/customHTTP";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import "./SingleProduct.css";
import { ASSETS_BASE_URL, SERVER_BASE_URL, RSPL_LIST } from "../../config";
import { useAuth } from "../../context/authContext";
import Swal from "sweetalert2";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PageNotFound from "../PageNotFound/PageNotFound";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DescriptionIcon from "@mui/icons-material/Description";

const SingleProduct = () => {
	const rsplMapping = Object.fromEntries(RSPL_LIST.flatMap(({ key, value }) => key.map((k) => [k, value])));
	const { machineID, productID } = useParams();
	const { addToCart } = useCart();
	const { selectedSite, siteData, selectedSiteCurrency, currencySymbol } = useSite();
	const [price, setPrice] = useState(0);
	const [product, setProduct] = useState(null);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(true);
	const [imageSrc, setImageSrc] = useState(`${ASSETS_BASE_URL}/stock.png`);
	const [quantity, setQuantity] = useState(1);
	const [isLoading, setIsLoading] = useState(false); // To manage the loading state
	const [isLoadingSFL, setIsLoadingSFL] = useState(false); // To manage the loading state
	const [isSuccess, setIsSuccess] = useState(false); // To track success state
	const [isSuccessSFL, setIsSuccessSFL] = useState(false); // To track success state
	const [alertisSuccess, setAlertIsSuccess] = useState(false); // To track success state
	const [alertMessage, setAlertMessage] = useState(""); // To track success state
	const [openAlert, setOpenAlert] = useState(false); // To control the alert visibility
	const [dialogOpen, setDialogOpen] = useState(false);
	const [lists, setLists] = useState([]);
	const [selectedList, setSelectedList] = useState("");
	const [newListName, setNewListName] = useState("");
	const [newListDescription, setNewListDescription] = useState("");
	const [newListError, setNewListError] = useState("");
	const [selectListError, setSelectListError] = useState("");
	const [has3DModel, setHas3DModel] = useState(false);
	const [has2DModel, setHas2DModel] = useState(false);
	const [machine_name, setmachine_name] = useState([]);
	const [machine_number, setmachine_number] = useState([]);
	const [m3DButtonLoading, setM3DButtonLoading] = useState(false);
	const { user } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		const fetchProduct = async () => {
			try {
				const reqBody = {
					machine_id: machineID,
					product_id: productID,
				};

				const response = await customHttp.post("/products/getsingle", reqBody);
				if (response.data.success && response.data.data.length > 0) {
					console.log(response.data.data[0]);
					setProduct(response.data.data[0]);
				} else {
					setError("Failed to fetch data: No product found.");
				}
			} catch (err) {
				setError("Failed to fetch data: " + err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchProduct();
	}, [productID, machineID]);

	useEffect(() => {
		const fetchMachineDetails = async () => {
			try {
				const reqBody = {
					machines: [machineID],
					// Additional filters can go here (e.g., selectedSite if needed)
				};

				const response = await customHttp.post("/machines/by-ids", reqBody);

				if (response.data.success) {
					// Extract machine_name and machine_number
					const machineData = response.data.data[0]; // Assuming you're getting one object, not an array of machines
					setmachine_name(machineData.machine_name);
					setmachine_number(machineData.machine_number);
				} else {
					setError("Failed to fetch data: No success flag.");
				}
			} catch (err) {
				setError("Failed to fetch data: " + err.message);
			}
		};

		fetchMachineDetails();
	}, [machineID]);

	useEffect(() => {
		const checkResources = async () => {
			const product3DModelUrl = `${SERVER_BASE_URL}/stock-models/3D/${machine_number}.html`;
			const product2DModelUrl = `${SERVER_BASE_URL}/stock-models/2D/${machine_number}.pdf`;

			try {
				// Check if the 3D model exists
				const modelResponse = await fetch(product3DModelUrl, { method: "HEAD" });
				if (modelResponse.ok) {
					setHas3DModel(true);
					setHas2DModel(false);
				} else {
					const model2dResponse = await fetch(product2DModelUrl, { method: "HEAD" });
					if (model2dResponse.ok) {
						setHas2DModel(true);
						setHas3DModel(false);
					} else {
						setHas2DModel(false);
						setHas3DModel(false);
					}
				}
			} catch (error) {
				console.warn("Resource check failed. Using defaults.");
			}
		};

		if (machine_number) {
			checkResources();
		}
	}, [machine_number]);

	useEffect(() => {
		const checkImageExists = async () => {
			if (product && product.product_name) {
				const productImageUrl = `${ASSETS_BASE_URL}/ProductImages/TMB/${product.product_number}.png`;
				try {
					const response = await fetch(productImageUrl, { method: "HEAD" });
					if (response.ok) {
						setImageSrc(productImageUrl);
					} else {
						console.warn("Product image not found, using default image.");
					}
				} catch (error) {
					console.warn("Product image fetch error, using default image.");
				}
			}
		};

		if (!loading && product) {
			checkImageExists();
		}
	}, [loading, product]);

	useEffect(() => {
		if (product && selectedSiteCurrency) {
			const key = `price_${selectedSiteCurrency}`;
			let currencyPrice = product[key];
			if (currencyPrice === "0") {
				currencyPrice = product.product_sell_price;
			}
			// If the product is purchasable, use the currency price; otherwise, set it to 0
			const finalPrice = product.purchasable ? currencyPrice : 0;

			setPrice(finalPrice);
		}
	}, [product, selectedSiteCurrency]);

	useEffect(() => {
		const fetchLists = async () => {
			try {
				const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;
				const user_id = user.user_id;
				const response = await customHttp.get(`/recommendations/${user_id}/${site_id}`);
				if (response.status === 200) {
					let recommendations = [];

					if (user.user_role !== "Admin") {
						recommendations = response.data.data.filter((item) => item.user_id === user.user_id);
					} else {
						recommendations = response.data.data;
					}
					if (recommendations.length === 0) setSelectedList("New");
					else setSelectedList(recommendations[0].id);
					setLists(recommendations);
				}
			} catch (error) {
				console.error("Error fetching lists:", error);
			}
		};

		if (selectedSite) fetchLists();
	}, [selectedSite]);

	const handleAddToRecommendationList = async () => {
		if (selectedList !== "new") {
			try {
				const response = await customHttp.post("/recommendations/products", {
					list_id: selectedList,
					products: [{ product_id: product.product_id, machine_id: machineID }],
				});
				if (response.status === 200) {
					setDialogOpen(false);
					console.log(response.data.results);
					if (response.data.results[0].status === "exists") {
						Swal.fire({
							icon: "info",
							title: "Info",
							text: "Part already present in the list",
						});
					} else {
						Swal.fire({
							icon: "success",
							title: "Success",
							text: "Part added to list",
						});
					}
				} else {
					console.error("Failed to add part to list");
				}
			} catch (error) {
				if (error.response.status === 409) {
					setSelectListError("This part is already added to the list.");
				}
				console.error("Error adding part to list:", error);
			}
		} else if (newListName) {
			// Create a new list
			const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;
			const user_id = user.user_id;

			try {
				const duplicateList = lists.some(
					(list) =>
						list.name.toLowerCase() === newListName.toLowerCase() &&
						list.user_id === user_id &&
						list.site_id === site_id
				);

				if (duplicateList) {
					setNewListError("A list with this name already exists.");
					return; // Stop further execution
				}
				const response = await customHttp.post("/recommendations", {
					name: newListName,
					description: newListDescription || "",
					user_id,
					site_id,
				});
				if (response.status === 200) {
					const newListId = response.data.data.id;
					const addProductResponse = await customHttp.post("/recommendations/products", {
						list_id: newListId,
						products: [{ product_id: product.product_id, machine_id: machineID }],
					});

					if (addProductResponse.status === 200) {
						setDialogOpen(false);
						Swal.fire({
							icon: "success",
							title: "Success",
							text: "New Recommendation List created and Product added to list",
						});
					} else {
						console.error("Failed to add product to the new list");
					}
				} else {
					console.error("Failed to create the list");
				}
			} catch (error) {
				console.error("Error creating new list:", error);
			}
		}
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setSelectedList("");
		setNewListName("");
		setNewListDescription("");
		setNewListError("");
	};

	const handleListChange = (event) => {
		setSelectListError();
		setSelectedList(event.target.value);
	};

	const handleNewListNameChange = (event) => {
		setNewListName(event.target.value);
		setNewListError("");
	};

	const handleNewListDescriptionChange = (event) => {
		setNewListDescription(event.target.value);
	};

	const handle3DButtonClick = async () => {
		setM3DButtonLoading(true); // Show loading state

		try {
			// Make the API call
			const response = await customHttp.get("/models/process-modelfile", {
				params: {
					filename: machine_number,
					machinename: machine_name,
					currency: selectedSiteCurrency.toUpperCase(),
					loggedinUser: user.user_id,
					ecommachineid: machineID,
					selected_part: product.product_number,
					selectedSite,
				},
			});

			if (response.status === 200) {
				const backendUrl = SERVER_BASE_URL || "";
				const fullUrl = backendUrl + response.data.url; // Construct the full URL
				setM3DButtonLoading(false);
				window.open(fullUrl); // Redirect after 1 second delay
			}
		} catch (err) {
			console.error("Error fetching the model:", err);
		}
	};

	const handleAddToRequest = async (event) => {
		event.stopPropagation();

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		setOpenAlert(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: machineID,
				product_id: product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await customHttp.post("/request_list/saverequestlist", req_body);
				if (response.data.success) {
					setIsSuccess(true);
					setIsLoading(false);
					setOpenAlert(true);
					setAlertIsSuccess(true);
					setAlertMessage(`Product ${product.product_name} added to Request List`);

					setTimeout(() => {
						setIsLoading(false);
						setIsSuccess(false);
						setOpenAlert(false);
					}, 1000);
				} else if (!response.data.success) {
					setOpenAlert(true);
					setAlertIsSuccess(false);
					setAlertMessage(`Product ${product.product_name} already in Request List`);
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			setOpenAlert(true); // Show error alert if desired
		} finally {
			setIsLoading(false);
		}
	};

	const handleSavedForLater = async (event) => {
		event.stopPropagation();

		// Start the loading state and reset success state
		setIsLoadingSFL(true);
		setIsSuccessSFL(false);
		setOpenAlert(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: machineID,
				product_id: product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await customHttp.post("/saved_for_later/saveproduct", req_body);
				if (response.data.success) {
					setIsSuccessSFL(true);
					setIsLoadingSFL(false);
					setOpenAlert(true);
					setAlertIsSuccess(true);
					setAlertMessage(`Product ${product.product_number} added to Saved for Later`);

					setTimeout(() => {
						setIsLoadingSFL(false);
						setIsSuccessSFL(false);
						setOpenAlert(false);
					}, 1000);
				} else if (!response.data.success) {
					setOpenAlert(true);
					setAlertIsSuccess(false);
					setAlertMessage(`Product ${product.product_number} already in Saved for Later`);
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccessSFL(false);
			setOpenAlert(true); // Show error alert if desired
		} finally {
			setIsLoadingSFL(false);
		}
	};

	const handleAddToCart = async (event) => {
		event.stopPropagation();

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);

		try {
			// Call the addToCart function (which you've defined to add the product)
			await addToCart({ ProductID: product.product_id, Quantity: quantity, Site: selectedSite });

			// Set success state
			setIsSuccess(true);
			setAlertIsSuccess(true);
			setAlertMessage(`Product ${product.product_name} Added to the Cart`);
			setOpenAlert(true); // Show success alert
			// Reset the button text back to "Add to Cart" after 2 seconds
			setTimeout(() => {
				setIsLoading(false);
				setIsSuccess(false);
				setOpenAlert(false);
			}, 2000); // 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			setOpenAlert(true); // Show error alert if desired
		} finally {
			// Stop the loading spinner after a delay and reset the button state
			setIsLoading(false);
		}
	};

	const handleQuantityChange = (event) => {
		const value = event.target.value;

		if (value === "") {
			setQuantity(1);
		} else {
			const newQuantity = Math.max(1, parseInt(value, 10));
			setQuantity(newQuantity);
		}
	};

	if (!machineID || !productID) {
		return <PageNotFound />;
	}

	return (
		<Container className="product-container" maxWidth="md">
			{loading && (
				<Box display="flex" justifyContent="center" className="loading-box">
					<CircularProgress />
				</Box>
			)}

			{error && (
				<Alert severity="error" className="error-message">
					{error}
				</Alert>
			)}

			{!loading && !error && product && (
				<Box className="product-details">
					<Box className="product-image-section">
						<img
							src={imageSrc}
							alt={product.product_name || "Default Product"}
							className="product-image-single"
						/>
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "center",
								marginTop: 2,
								width: "100%",
							}}>
							{has3DModel && (
								<Button
									startIcon={<ViewInArIcon />}
									onClick={() => handle3DButtonClick()}
									sx={{ flex: 1 }}>
									{m3DButtonLoading ? (
										<>
											Loading
											<CircularProgress color="white" size={20} sx={{ marginLeft: "5px" }} />{" "}
										</>
									) : (
										"Open 3d Model"
									)}
								</Button>
							)}
							<Button
								sx={{ flex: 1 }}
								startIcon={<DescriptionIcon />}
								onClick={() => navigate(`/documents/${machineID}`)}>
								Documentation
							</Button>
						</Box>
					</Box>
					<Box className="product-info-section">
						<Typography variant="h4" className="product-name">
							{product.product_name}
						</Typography>
						<div className="header-divider"></div>
						<Typography variant="body1" className="product-description">
							<b>Machine #:</b> {machine_name} | <b>Part #:</b> {product.product_number}
						</Typography>
						{product.purchasable === 1 && price !== 0 && (
							<>
								<Typography variant="h4" color="primary" className="product-price">
									{currencySymbol}
									{price}
								</Typography>
								<Box className="product-quantity" marginY={2}>
									<Typography variant="body2" className="quantity-label">
										Qty:
									</Typography>
									<TextField
										type="number"
										variant="outlined"
										size="small"
										value={quantity}
										onChange={handleQuantityChange}
										slotProps={{
											input: {
												min: 1, // Ensure the minimum value is set to 1
											},
										}}
										className="quantity-input"
									/>
								</Box>
								{/* <Typography
									sx={{ margin: "10px 0 5px 0" }}
									variant="body1"
									color={product.stock > 0 ? "green" : "error"}>
									{product.stock > 0 ? `In Stock [ ${product.stock} ]` : `Out Of Stock`}
								</Typography> */}
							</>
						)}

						{product.purchasable === 1 ? (
							<Box>
								<Button
									size="small"
									variant="contained"
									sx={{
										backgroundColor: "var(--primary-main)",
										padding: "8px 15px",
										fontWeight: "bolder",
										width: "fit-content",
										mt: 2,
									}}
									onClick={handleAddToCart}
									disabled={isLoading}
									className="button-primary" // Disable the button while loading or after success
								>
									{isLoading ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											Adding...
										</>
									) : isSuccess ? (
										<>
											<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
											Added
										</>
									) : (
										"Add to Cart"
									)}
								</Button>

								<Button
									size="small"
									variant="outlined"
									sx={{
										padding: "8px 15px",
										fontWeight: "bolder",
										mt: 2,
										ml: 2,
									}}
									onClick={handleSavedForLater}
									disabled={isLoadingSFL}
									startIcon={<BookmarkIcon />}
									className="button-outline" // Disable the button while loading or after success
								>
									{isLoadingSFL ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											Saving...
										</>
									) : isSuccessSFL ? (
										<>
											<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
											Saved
										</>
									) : (
										"Save for Later"
									)}
								</Button>
							</Box>
						) : (
							<>
								<Button
									size="small"
									variant="contained"
									sx={{
										backgroundColor: "var(--primary-main)",
										padding: "8px 15px",
										fontWeight: "bolder",
										mt: 2,
									}}
									onClick={handleAddToRequest}
									disabled={isLoading}
									className="button-primary" // Disable the button while loading or after success
								>
									{isLoading ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											Adding...
										</>
									) : isSuccess ? (
										<>
											<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
											Added
										</>
									) : (
										"Add to Request List"
									)}
								</Button>
								<Typography variant="h6" color="error">
									Please select “Add to Request List” for additional information on this part.
								</Typography>
							</>
						)}
						<Divider sx={{ mt: 4 }} />
						{/* <Typography variant="body1" className="product-description">
							Description: {product.product_description}
						</Typography> */}
						<Typography variant="body1" className="product-description">
							<b>Part Type:</b>{" "}
							{rsplMapping[product.product_rspl_code] ? rsplMapping[product.product_rspl_code] : "Other"}{" "}
							| <b>Where Used:</b> {machine_name}
						</Typography>
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-start",
								alignItems: "center",
								marginTop: 2,
								width: "100%",
								columnGap: 2,
							}}>

								<Button
									size="small"
									variant="outlined"
									sx={{
										padding: "8px 15px",
										fontWeight: "bolder",
										width: "fit-content",
									}}
									onClick={() => setDialogOpen(true)}>
									Add to My List
								</Button>


							<Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
								<DialogTitle>Add Product to List</DialogTitle>
								<DialogContent>
									<FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
										<InputLabel id="select-list-label">Select List</InputLabel>
										<Select
											labelId="select-list-label"
											value={selectedList}
											onChange={handleListChange}
											fullWidth
											label="Select List"
											size="small"
											error={!!selectListError}
											helperText={selectListError}>
											{lists.map((list) => (
												<MenuItem key={list.id} value={list.id}>
													{list.name}
												</MenuItem>
											))}
											<MenuItem value="new"> + Create New List</MenuItem>
										</Select>
										{!!selectListError && <FormHelperText error>{selectListError}</FormHelperText>}
									</FormControl>

									{selectedList === "new" && (
										<>
											<TextField
												label="List Name"
												value={newListName}
												onChange={handleNewListNameChange}
												fullWidth
												size="small"
												required
												error={!!newListError}
												helperText={newListError || "Enter a unique name"}
												sx={{ mb: 2 }}
											/>
											<TextField
												label="List Description"
												value={newListDescription}
												size="small"
												onChange={handleNewListDescriptionChange}
												fullWidth
												sx={{ mb: 2 }}
											/>
										</>
									)}
								</DialogContent>
								<DialogActions>
									<Button onClick={handleCloseDialog} color="secondary">
										Cancel
									</Button>
									<Button
										onClick={handleAddToRecommendationList}
										color="primary"
										disabled={isLoading || (!selectedList && !newListName)}>
										{isLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Add"}
									</Button>
								</DialogActions>
							</Dialog>
						</Box>
					</Box>
				</Box>
			)}
			<Snackbar
				open={openAlert}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				autoHideDuration={3000}
				onClose={() => setOpenAlert(false)}
				className="SnackBar">
				<Alert severity={alertisSuccess ? "success" : "error"} sx={{ width: "100%" }}>
					{alertMessage}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default SingleProduct;
